import React, { useState, useRef, useCallback } from 'react';
import { Mic, X } from 'lucide-react';
import './MessageInput.css';

const MAX_INPUT_HEIGHT = 120; // keep in sync with .message-input max-height

const MessageInput = ({ onSendMessage, disabled, suggestedChips, onOpenVoice, focusLabel, onClearFocus }) => {
  const [message, setMessage] = useState('');
  const textareaRef = useRef(null);

  // A one-row textarea that never grows means a phone user composing three
  // lines can only ever see one of them. Grow with the content, up to the cap.
  const resize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_INPUT_HEIGHT)}px`;
  }, []);

  const handleChange = (e) => {
    setMessage(e.target.value);
    resize();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim() && !disabled) {
      onSendMessage(message.trim());
      setMessage('');
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    }
  };

  const handleChip = (text) => {
    if (text && !disabled) {
      onSendMessage(text);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="message-input-wrapper">
      {/* The subject the agent is bound to. With one continuous session across
          screens, this is the only signal of what "it" resolves to — so it is
          load-bearing, not decoration. */}
      {focusLabel && (
        <div className="focus-pill-row">
          <span className="focus-pill">
            <span className="focus-pill-text">
              Talking about <span className="focus-pill-name">{focusLabel}</span>
            </span>
            {onClearFocus && (
              <button
                type="button"
                className="focus-pill-clear"
                onClick={onClearFocus}
                title="Back to general chat"
                aria-label="Back to general chat"
              >
                <X size={13} />
              </button>
            )}
          </span>
        </div>
      )}
      {suggestedChips && suggestedChips.length > 0 && (
        <div className="suggested-chips">
          {suggestedChips.slice(0, 4).map((chip, i) => (
            <button
              key={i}
              type="button"
              className="suggested-chip"
              onClick={() => handleChip(chip)}
              disabled={disabled}
            >
              {chip}
            </button>
          ))}
        </div>
      )}
    <form className="message-input-container" onSubmit={handleSubmit}>
      {onOpenVoice && (
        <button
          type="button"
          className="mic-button"
          onClick={onOpenVoice}
          disabled={disabled}
          aria-label="Start voice conversation"
          title="Talk instead of type"
        >
          <Mic size={18} />
        </button>
      )}
      <textarea
        ref={textareaRef}
        className="message-input"
        value={message}
        onChange={handleChange}
        onKeyDown={handleKeyPress}
        enterKeyHint="send"
        autoComplete="off"
        autoCapitalize="sentences" 
        placeholder={disabled ? "Reconnecting\u2026" : "What are you looking for?"}
        rows="1"
        disabled={disabled}
      />
      <button 
        type="submit" 
        className="send-button"
        disabled={disabled || !message.trim()}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
        </svg>
      </button>
    </form>
    </div>
  );
};

export default MessageInput;
